import { DollarSign, ShoppingBag, Users, Package, TrendingUp, Bell, Loader2, AlertTriangle, Star, Mail, Gift, Ticket } from "lucide-react";

function formatPrice(p) {
  return "$" + (typeof p === "number" ? p : 0).toFixed(2);
}

export default function AnalyticsTab({ analytics, error }) {
  if (!analytics && !error) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 text-[#C0A062] animate-spin" />
      </div>
    );
  }

  var s = (analytics && analytics.summary) || {};
  var topProducts = (analytics && analytics.top_products) || [];

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-950/40 border border-red-500/40 rounded-sm p-4 flex items-start gap-3" data-testid="analytics-error">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-heading font-bold text-red-300 text-sm uppercase">Analytics failed to load</p>
            <p className="text-red-200 text-xs mt-1 font-mono break-all">{error}</p>
            <p className="text-red-300/70 text-xs mt-2">Showing empty data. Forward this error to support if it persists after a redeploy.</p>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[#121212] border border-white/5 rounded-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-sm bg-[#C0A062]/20"><DollarSign className="w-5 h-5 text-[#C0A062]" /></div>
            <span className="text-[#71717a] text-sm font-mono uppercase">Total Revenue</span>
          </div>
          <p className="text-3xl font-heading font-bold text-white">{formatPrice(s.total_revenue)}</p>
          <p className="text-sm text-[#71717a] mt-1">{formatPrice(s.month_revenue)} this month</p>
        </div>
        <div className="bg-[#121212] border border-white/5 rounded-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-sm bg-[#C0A062]/20"><ShoppingBag className="w-5 h-5 text-[#C0A062]" /></div>
            <span className="text-[#71717a] text-sm font-mono uppercase">Total Orders</span>
          </div>
          <p className="text-3xl font-heading font-bold text-white">{s.total_orders}</p>
          <p className="text-sm text-[#71717a] mt-1">{s.month_orders} this month</p>
        </div>
        <div className="bg-[#121212] border border-white/5 rounded-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-sm bg-[#C0A062]/20"><Users className="w-5 h-5 text-[#C0A062]" /></div>
            <span className="text-[#71717a] text-sm font-mono uppercase">Customers</span>
          </div>
          <p className="text-3xl font-heading font-bold text-white">{s.total_customers}</p>
        </div>
        <div className="bg-[#121212] border border-white/5 rounded-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-sm bg-[#C0A062]/20"><Package className="w-5 h-5 text-[#C0A062]" /></div>
            <span className="text-[#71717a] text-sm font-mono uppercase">Products</span>
          </div>
          <p className="text-3xl font-heading font-bold text-white">{s.total_products}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#121212] border border-white/5 rounded-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-sm bg-green-500/20"><DollarSign className="w-5 h-5 text-white" /></div>
            <span className="text-[#71717a] text-sm font-mono uppercase">Today</span>
          </div>
          <p className="text-3xl font-heading font-bold text-white">{formatPrice(s.today_revenue)}</p>
          <p className="text-sm text-[#71717a] mt-1">{s.today_orders} orders</p>
        </div>
        <div className="bg-[#121212] border border-white/5 rounded-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-sm bg-blue-500/20"><TrendingUp className="w-5 h-5 text-white" /></div>
            <span className="text-[#71717a] text-sm font-mono uppercase">This Week</span>
          </div>
          <p className="text-3xl font-heading font-bold text-white">{formatPrice(s.week_revenue)}</p>
          <p className="text-sm text-[#71717a] mt-1">{s.week_orders} orders</p>
        </div>
        <div className="bg-[#121212] border border-white/5 rounded-sm p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-sm bg-yellow-500/20"><Bell className="w-5 h-5 text-white" /></div>
            <span className="text-[#71717a] text-sm font-mono uppercase">Waiting Notifications</span>
          </div>
          <p className="text-3xl font-heading font-bold text-white">{s.pending_notifications}</p>
          <p className="text-sm text-[#71717a] mt-1">customers waiting for restock</p>
        </div>
      </div>
      <div className="bg-[#121212] border border-white/5 rounded-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-sm bg-[#C0A062]/20"><Star className="w-5 h-5 text-[#C0A062]" /></div>
            <h3 className="font-heading font-bold text-white text-lg uppercase">Review Funnel</h3>
          </div>
          <span className="text-[#71717a] text-xs font-mono uppercase" data-testid="review-response-rate">
            {s.review_response_rate || 0}% response rate
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="border-l-2 border-[#C0A062]/30 pl-4" data-testid="stat-review-emails-sent">
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-3.5 h-3.5 text-[#71717a]" />
              <span className="text-[#71717a] text-[10px] font-mono uppercase tracking-wider">Emails Sent</span>
            </div>
            <p className="text-2xl font-heading font-bold text-white">{s.review_emails_sent || 0}</p>
            <p className="text-[10px] text-[#71717a]">review requests</p>
          </div>
          <div className="border-l-2 border-[#C0A062]/30 pl-4" data-testid="stat-reviews-received">
            <div className="flex items-center gap-2 mb-1">
              <Star className="w-3.5 h-3.5 text-[#71717a]" />
              <span className="text-[#71717a] text-[10px] font-mono uppercase tracking-wider">Reviews</span>
            </div>
            <p className="text-2xl font-heading font-bold text-white">{s.total_reviews || 0}</p>
            <p className="text-[10px] text-[#71717a]">posted by buyers</p>
          </div>
          <div className="border-l-2 border-[#C0A062]/30 pl-4" data-testid="stat-rewards-issued">
            <div className="flex items-center gap-2 mb-1">
              <Gift className="w-3.5 h-3.5 text-[#71717a]" />
              <span className="text-[#71717a] text-[10px] font-mono uppercase tracking-wider">Rewards Issued</span>
            </div>
            <p className="text-2xl font-heading font-bold text-white">{s.rewards_issued || 0}</p>
            <p className="text-[10px] text-[#71717a]">10% codes minted</p>
          </div>
          <div className="border-l-2 border-[#8CB369]/40 pl-4" data-testid="stat-rewards-redeemed">
            <div className="flex items-center gap-2 mb-1">
              <Ticket className="w-3.5 h-3.5 text-[#8CB369]" />
              <span className="text-[#8CB369] text-[10px] font-mono uppercase tracking-wider">Redeemed</span>
            </div>
            <p className="text-2xl font-heading font-bold text-[#8CB369]">{s.rewards_redeemed || 0}</p>
            <p className="text-[10px] text-[#71717a]">codes used at checkout</p>
          </div>
        </div>
      </div>
      <div className="bg-[#121212] border border-white/5 rounded-sm p-6">
        <h3 className="font-heading font-bold text-white text-lg mb-4">Top Selling Products</h3>
        {topProducts.length > 0 ? (
          <div className="space-y-3">
            {topProducts.slice(0, 10).map(function(p, i) {
              return (
                <div key={p.product_id || p.name} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="text-[#C0A062] font-mono text-sm">#{i+1}</span>
                    <span className="text-white">{p.name}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-[#71717a] text-sm">{p.quantity} sold</span>
                    <span className="text-[#C0A062] font-heading font-bold">{formatPrice(p.revenue)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-[#71717a] text-center py-8">No sales yet</p>
        )}
      </div>
    </div>
  );
}
